import API_BASE from '../config';
import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell,
    LineChart, Line, AreaChart, Area, ReferenceLine
} from "recharts";
import {
  ShieldCheck, Zap, Loader2, Globe, ShieldAlert, Clock, RefreshCw,
    TrendingUp, TrendingDown, BarChart2, ChevronRight, PlayCircle,
  FileCheck, Shield, BarChart3, Lock
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { TrialBanner } from "../components/TrialComponents";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getSymbol = (code: string | undefined): string => {
  const map: Record<string, string> = {
    "USD": "$", "GBP": "£", "EUR": "€", "CAD": "C$",
    "AUD": "A$", "JPY": "¥", "SGD": "S$", "CHF": "CHF",
    "NGN": "₦", "ZAR": "R", "INR": "₹", "AED": "إ. د"
  };
  return code ? (map[code] || "$") : "$";
};

const formatExposure = (value: number, sym: string) => {
  if (value >= 1_000_000) return `${sym}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000)     return `${sym}${(value / 1_000).toFixed(1)}K`;
  return `${sym}${value.toLocaleString()}`;
};

const nistColor = (tier: number) => {
  if (tier === 4) return { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200", accent: "#10b981" };
  if (tier === 3) return { bg: "bg-blue-50",    text: "text-blue-600",    border: "border-blue-200",    accent: "#3b82f6" };
  if (tier === 2) return { bg: "bg-amber-50",   text: "text-amber-600",   border: "border-amber-200",   accent: "#f59e0b" };
  return             { bg: "bg-red-50",      text: "text-red-600",     border: "border-red-200",     accent: "#dc2626" };
};

// ─── Animation variants ───────────────────────────────────────────────────────

const fadeUp = {
  initial:    { opacity: 0, y: 16 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.3     },
};

const stagger = (i: number) => ({
  initial:    { opacity: 0, y: 12 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.3, delay: i * 0.06 },
});

// ─── RoadmapCard ──────────────────────────────────────────────────────────────

interface RoadmapProps {
  title:        string;
  reduction:    string;
  detail:       string;
  currency?:    string;
  roi_note?:    string;
  cis_control?: string;
  accentColor?: "red" | "slate";
}

function RoadmapCard({
  title, reduction, detail, currency, roi_note, cis_control, accentColor = "red",
}: RoadmapProps) {
  const clean = reduction.toString()
    .replace(/^(CHF|C\$|A\$|S\$|د\.إ|£|€|\$|₦|R|₹|¥)/g, "")
    .trim();

  // Extract priority from detail string e.g. "CRITICAL Priority"
  const priority = detail.replace(" Priority", "").toUpperCase().trim();

  // Priority color system
  const priorityConfig: Record<string, {
    badge: string;
    border: string;
    dot: string;
    label: string;
  }> = {
    CRITICAL: {
      badge: "bg-red-100 text-red-700 border border-red-200",
      border: "border-l-red-600",
      dot:   "bg-red-600",
      label: "CRITICAL",
    },
    HIGH: {
      badge: "bg-orange-100 text-orange-700 border border-orange-200",
      border: "border-l-orange-500",
      dot:   "bg-orange-500",
      label: "HIGH",
    },
    MEDIUM: {
      badge: "bg-amber-100 text-amber-700 border border-amber-200",
      border: "border-l-amber-400",
      dot:   "bg-amber-400",
      label: "MEDIUM",
    },
    LOW: {
      badge: "bg-blue-100 text-blue-700 border border-blue-200",
      border: "border-l-blue-400",
      dot:   "bg-blue-400",
      label: "LOW",
    },
  };

  const pc = priorityConfig[priority] || priorityConfig["MEDIUM"];
  const accentText = accentColor === "slate" ? "text-slate-600" : "text-red-600";

  return (
    <div className={`bg-[#0f172a] border border-white/10 border-l-[6px] ${pc.border}
      p-8 rounded-3xl flex flex-col justify-between
      hover:bg-[#1e293b] transition-all text-left gap-5`}>
      <div>
        <div className="flex justify-between items-start mb-3">
          <h4 className="text-sm font-black tracking-tight text-white uppercase
            leading-tight flex-1 pr-3">
            {title}
          </h4>
          {cis_control && (
            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400
              bg-white/10 px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0">
              {cis_control}
            </span>
          )}
        </div>

        {/* Priority badge with color */}
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg
            text-[9px] font-black uppercase tracking-widest ${pc.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${pc.dot}`} />
            {pc.label} PRIORITY
          </span>
        </div>

        {roi_note && (
          <p className="text-slate-400 text-[10px] font-medium mt-2 leading-relaxed italic">
            {roi_note}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className={`text-[9px] font-black uppercase tracking-widest mb-1 ${accentText}`}>
          Est. Implementation
        </p>
        <p className="text-3xl font-black text-white tracking-tighter">
          {getSymbol(currency)}{clean}
        </p>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const [selectedEntity, setSelectedEntity] = React.useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["assessment", selectedEntity],
    queryFn:  () => {
      const url = selectedEntity
        ? `${API_BASE}/assessment/latest?entity=${encodeURIComponent(selectedEntity)}`
        : `${API_BASE}/assessment/latest`;
      return axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data);
    },
    refetchOnWindowFocus: true,
    enabled: !!localStorage.getItem("token"),
    retry: 2,
  });

  const { data: userData } = useQuery({
    queryKey: ["userProfile"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data),
  });

  const { data: riskHistory = [] } = useQuery({
    queryKey: ["risk-history"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/risk/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data),
    enabled: !!localStorage.getItem("token"),
    refetchOnWindowFocus: true,
  });

  const currency      = data?.form_data?.revenue_currency || "USD";
  const symbol        = getSymbol(currency);
  const ci            = data?.risk?.confidence_interval || { expected: 0, p90: 0, p99: 0 };
  const nistTier      = data?.risk?.nist_tier           || { tier: 1, label: "Partial", description: "" };
  const benchmarking  = data?.risk?.benchmarking        || { percentile: 0, sector_rank: "N/A", sector_avg_breach: 0, vs_sector_avg: "N/A" };
  const nc            = nistColor(nistTier.tier);
  const scannedDomain = data?.scan_results?.domain || data?.external_threats?.domain || "domain.com";
  const [rescanning, setRescanning] = React.useState(false);
  const handleRescan = async () => {
    setRescanning(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["assessment"] });
    } finally {
      setTimeout(() => setRescanning(false), 3000);
    }
  };
  const totalExposure = data?.risk?.total_exposure       || 0;
  const riskScore     = data?.risk?.risk_score           || 0;

  const chartData = data?.risk?.scenarios
    ? Object.entries(data.risk.scenarios)
        .map(([key, val]: any) => ({
          name:       key.replace(/_/g, " ").toUpperCase(),
          impact:     val.annual_loss || 0,
          exposure:   val.exposure   || 0,
          probability: val.probability || 0,
        }))
    : [];

  // Dynamic color based on % of max loss — matches PDF report
  const getBarColor = (impact: number): string => {
    const maxImpact = chartData.length > 0
      ? Math.max(...chartData.map((d: any) => d.impact))
      : 1;
    if (maxImpact === 0) return "#6366f1";
    const pct = impact / maxImpact;
    if (pct >= 0.80) return "#dc2626"; // CRITICAL — Red
    if (pct >= 0.60) return "#ea580c"; // HIGH — Orange
    if (pct >= 0.40) return "#d97706"; // ELEVATED — Amber
    if (pct >= 0.20) return "#ca8a04"; // MODERATE — Yellow
    if (pct >= 0.10) return "#16a34a"; // LOW — Green
    return "#0369a1";                  // MINIMAL — Blue
  };

  const navigate      = useNavigate();
  const hasAssessment = !!(data?.form_data?.company_name);

  const entities: string[] = React.useMemo(() => {
    if (!userData?.entities) return [];
    return userData.entities.map((e: any) => e.company_name).filter(Boolean);
  }, [userData]);

  const handleEntitySwitch = (entityName: string) => {
    setSelectedEntity(entityName);
  };

  // ── Payment success detection ─────────────────────────────────
  const [showPaymentSuccess, setShowPaymentSuccess] = React.useState(false);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "success") {
      setShowPaymentSuccess(true);
      // Clean URL
      window.history.replaceState({}, "", "/dashboard");
      // Refresh user data to get new tier
      setTimeout(() => setShowPaymentSuccess(false), 6000);
    }
  }, []);

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
      <Loader2 size={36} className="animate-spin text-red-600" />
    </div>
  );

  if (isError) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 bg-red-50 rounded-[1.5rem] flex items-center
          justify-center mx-auto mb-4">
          <ShieldAlert size={28} className="text-red-500" />
        </div>
        <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight mb-2">
          Unable To Load Dashboard
        </h2>
        <p className="text-sm text-slate-500 font-medium mb-6">
          There was a problem loading your data. Please refresh the page or contact
          support@vicshield.com if the issue persists.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 text-white px-6 py-3 rounded-xl font-black
            text-[10px] uppercase tracking-widest hover:bg-slate-900 transition-all">
          Refresh Page
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-4 bg-[#f8fafc] min-h-screen font-['Inter'] text-left">

      {/* ── ONBOARDING BANNER — shows only when no assessment exists ── */}
      <AnimatePresence>
        {/* Trial Banner */}
        <TrialBanner />

                {!hasAssessment && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="bg-[#0f172a] rounded-[2rem] p-8 relative overflow-hidden border-l-[8px] border-red-600"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600 opacity-[0.06]
              rounded-full blur-[80px] pointer-events-none" />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center
                  justify-center flex-shrink-0 shadow-lg shadow-red-900/30">
                  <PlayCircle size={24} className="text-white" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-1">
                    Welcome to VicShield
                  </p>
                  <h2 className="text-xl font-black text-white uppercase tracking-tight mb-2">
                    Start Your First Intelligence Audit
                  </h2>
                  <p className="text-slate-400 text-sm font-medium leading-relaxed max-w-xl">
                    Your dashboard is ready. Complete your first Cyber Risk Assessment
                    to generate your Risk Resilience Score, Financial Exposure figure
                    and Strategic Remediation Plan.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-3 flex-shrink-0">
                {[
                  { icon: FileCheck, label: "Complete 16-stage assessment" },
                  { icon: Shield,    label: "Get your risk score instantly" },
                  { icon: BarChart3, label: "Download your audit report"    },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <step.icon size={13} className="text-slate-400" />
                    </div>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                      {step.label}
                    </span>
                  </div>
                ))}
                <button
                  onClick={() => navigate("/assessment")}
                  className="mt-2 bg-red-600 text-white px-7 py-3.5 rounded-2xl
                    font-black text-[10px] uppercase tracking-widest
                    hover:bg-white hover:text-red-600 transition-all shadow-lg">
                  Begin Assessment →
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── COMMAND CENTER HERO ────────────────────────────────────── */}
      {hasAssessment && (
        <motion.div {...fadeUp}
          className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
            rounded-[2rem] md:rounded-[3rem] p-5 md:p-8 shadow-2xl border border-white/5">

          <div className="absolute top-0 right-0 w-96 h-96 bg-red-600 opacity-[0.04]
            rounded-full blur-[120px] pointer-events-none" />

          {/* Top row */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-xl">
                  <span className="text-white font-black text-[10px] tracking-widest uppercase">
                    {userData?.tag_id || "VS-AUTH"}
                  </span>
                </div>
              </div>
              <h1 className="text-3xl font-black text-white uppercase tracking-tight">
                {data?.form_data?.company_name || "Enterprise Portal"}
              </h1>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 flex items-center gap-2">
                <Globe size={11} />
                {data?.form_data?.business_sector || "Initializing"} · Global Intelligence Active
              </p>
            </div>
            {nistTier.tier > 0 && (
              <div className={`px-5 py-3 rounded-2xl border ${nc.bg} ${nc.border} flex-shrink-0`}>
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-0.5">
                  NIST CSF Maturity
                </p>
                <p className={`text-base font-black uppercase ${nc.text}`}>
                  Tier {nistTier.tier} — {nistTier.label}
                </p>
              </div>
            )}
          </div>

          {/* Main metrics grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <p className="text-[9px] font-black uppercase tracking-widest text-red-400 mb-2">Security Posture</p>
              <div className="flex items-baseline gap-1">
                <span className={`text-4xl font-black tracking-tighter ${
                  riskScore >= 80 ? "text-emerald-400" :
                  riskScore >= 50 ? "text-amber-400" : "text-red-400"
                }`}>{riskScore}</span>
                <span className="text-slate-500 text-lg font-bold">/100</span>
              </div>
              <div className="mt-2 w-full bg-white/10 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${
                  riskScore >= 80 ? "bg-emerald-500" :
                  riskScore >= 50 ? "bg-amber-500" : "bg-red-500"
                }`} style={{ width: `${riskScore}%` }} />
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-5 overflow-hidden">
              <p className="text-[9px] font-black uppercase tracking-widest text-red-400 mb-2">Annual Exposure</p>
              <div className="flex items-baseline gap-1 flex-wrap">
                <span className="text-slate-400 text-lg font-black">{symbol}</span>
                <span className="text-2xl sm:text-3xl md:text-4xl font-black text-white tracking-tighter">
                  {totalExposure >= 1_000_000
                    ? `${(totalExposure / 1_000_000).toFixed(1)}M`
                    : totalExposure >= 1_000
                    ? `${(totalExposure / 1_000).toFixed(1)}K`
                    : totalExposure.toLocaleString()}
                </span>
              </div>
              <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-2">Actuarial projection</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <p className="text-[9px] font-black uppercase tracking-widest text-amber-400 mb-2">P90 Worst Case</p>
              <div className="flex items-baseline gap-1">
                <span className="text-slate-400 text-lg font-black">{symbol}</span>
                <span className="text-4xl font-black text-white tracking-tighter">
                  {ci.p90 >= 1_000_000
                    ? `${(ci.p90 / 1_000_000).toFixed(1)}M`
                    : ci.p90 >= 1_000
                    ? `${(ci.p90 / 1_000).toFixed(1)}K`
                    : ci.p90.toLocaleString()}
                </span>
              </div>
              <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-2">10% chance exceeding</p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
              <p className="text-[9px] font-black uppercase tracking-widest text-red-400 mb-2">P99 Extreme Case</p>
              <div className="flex items-baseline gap-1">
                <span className="text-slate-400 text-lg font-black">{symbol}</span>
                <span className="text-4xl font-black text-white tracking-tighter">
                  {ci.p99 >= 1_000_000
                    ? `${(ci.p99 / 1_000_000).toFixed(1)}M`
                    : ci.p99 >= 1_000
                    ? `${(ci.p99 / 1_000).toFixed(1)}K`
                    : ci.p99.toLocaleString()}
                </span>
              </div>
              <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-2">Worst case 1% probability</p>
            </div>

            {(data?.scan_results?.cve_findings?.length ?? 0) > 0 && (
              <div className="bg-red-900/30 border border-red-500/30 rounded-2xl p-5">
                <p className="text-[9px] font-black uppercase tracking-widest text-red-400 mb-2">CVE Exposure</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-slate-400 text-lg font-black">{symbol}</span>
                  <span className="text-4xl font-black text-red-400 tracking-tighter">
                    {(() => {
                      const total = data.scan_results.cve_findings.reduce((sum: number, c: any) => sum + (c.est_cost_max || 0), 0);
                      return total >= 1_000_000
                        ? `${(total / 1_000_000).toFixed(1)}M`
                        : total >= 1_000
                        ? `${(total / 1_000).toFixed(1)}K`
                        : total.toLocaleString();
                    })()}
                  </span>
                </div>
                <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-2">
                  {data.scan_results.cve_findings.length} vulnerabilities detected
                </p>
              </div>
            )}
          </div>

          {/* Entity switcher */}
          {entities.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">Switch Entity:</p>
              {entities.map((entity: string) => (
                <button key={entity}
                  onClick={() => handleEntitySwitch(entity)}
                  className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                    (selectedEntity || data?.form_data?.company_name) === entity
                      ? "bg-red-600 text-white"
                      : "bg-white/10 text-slate-400 hover:bg-white/20"
                  }`}>
                  {entity}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}

        {/* ── 4. RISK SCORE TREND ─────────────────────────────────────── */}
      {riskHistory.length > 1 && (() => {
        const latest  = riskHistory[riskHistory.length - 1]?.risk_score || 0;
        const previous = riskHistory[riskHistory.length - 2]?.risk_score || 0;
        const trend   = latest - previous;
        const improving = trend >= 0;
        return (
          <motion.div {...stagger(3)}
            className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
              rounded-[2.5rem] p-8 shadow-2xl border border-white/5">

            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-1">
                  Posture Intelligence
                </p>
                <h3 className="text-xl font-black text-white uppercase tracking-tight">
                  Risk Score Trend
                </h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                  Historical posture over time
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">
                    Current Score
                  </p>
                  <p className={`text-3xl font-black tracking-tighter ${
                    latest >= 80 ? "text-emerald-400" :
                    latest >= 50 ? "text-amber-400" : "text-red-400"
                  }`}>{latest}<span className="text-lg text-slate-500">/100</span></p>
                </div>
                <div className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border ${
                  improving
                    ? "bg-emerald-900/30 border-emerald-800/30 text-emerald-400"
                    : "bg-red-900/30 border-red-800/30 text-red-400"
                }`}>
                  {improving
                    ? <TrendingUp size={16} />
                    : <TrendingDown size={16} />
                  }
                  <span className="text-[10px] font-black uppercase tracking-widest">
                    {improving ? "+" : ""}{trend} pts
                  </span>
                </div>
              </div>
            </div>

            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={riskHistory} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="riskGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#dc2626" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#dc2626" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff10" />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 9, fontWeight: 700 }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#64748b", fontSize: 9, fontWeight: 700 }}
                  />
                  <ReferenceLine y={50} stroke="#ffffff15" strokeDasharray="4 4" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "12px",
                      boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
                      fontFamily: "Inter, sans-serif",
                      color: "#ffffff",
                    }}
                    formatter={(v: any) => [`${v}/100`, "Risk Score"]}
                    labelStyle={{ color: "#94a3b8", fontSize: 10 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="risk_score"
                    stroke="#dc2626"
                    strokeWidth={2.5}
                    fill="url(#riskGradient)"
                    dot={{ fill: "#dc2626", r: 4, strokeWidth: 0 }}
                    activeDot={{ r: 6, fill: "#dc2626", stroke: "#fff", strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        );
      })()}

      <motion.div {...stagger(3)}
        className="bg-[#0f172a] rounded-[2.5rem] p-10 border-l-[8px] border-red-600 shadow-xl">
        <div className="flex items-center gap-4 mb-7">
          <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <BarChart2 size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-black text-white uppercase tracking-tight">
              Sector Intelligence Benchmark
            </h3>
            <p className="text-slate-500 text-[9px] font-black uppercase tracking-widest mt-0.5">
              IBM Cost of a Data Breach 2024 & Verizon DBIR 2024
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { label: "Sector Percentile",     value: `${benchmarking.percentile}%`,                          sub: benchmarking.sector_rank },
            { label: "Sector Avg Breach Cost", value: formatExposure(benchmarking.sector_avg_breach, symbol), sub: `${data?.form_data?.business_sector || "General"} Sector` },
            { label: "Posture vs Sector",      value: null,                                                   sub: benchmarking.vs_sector_avg },
          ].map((b, i) => (
            <div key={i} className="bg-white/5 rounded-2xl p-6 border border-white/8">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-3">
                {b.label}
              </p>
              {b.value && (
                <p className="text-3xl font-black text-white tracking-tighter mb-1">{b.value}</p>
              )}
              <p className={`font-bold uppercase tracking-widest leading-snug ${
                b.value ? "text-[9px] text-slate-400" : "text-xs text-white"
              }`}>
                {b.sub}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── 5. CHART + INBOX ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Bar chart */}
        <motion.div {...stagger(4)}
          className="lg:col-span-2 relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
            rounded-[2.5rem] p-8 shadow-2xl border border-white/5">

          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-1">
                Financial Intelligence
              </p>
              <h3 className="text-xl font-black text-white uppercase tracking-tight">
                Threat Vector Impact
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                Expected Annual Loss ({symbol})
              </p>
            </div>
            <div className="flex gap-3 flex-wrap justify-end">
              {[
                { color: "#dc2626", label: "Critical (≥80%)" },
                { color: "#ea580c", label: "High (≥60%)" },
                { color: "#d97706", label: "Elevated (≥40%)" },
                { color: "#16a34a", label: "Low (≥10%)" },
                { color: "#0369a1", label: "Minimal (<10%)" },
              ].map((l, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                  <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff08" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#64748b", fontSize: 9, fontWeight: 900 }}
                  angle={-40}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#64748b", fontSize: 10, fontWeight: 700 }}
                  tickFormatter={(value: number) =>
                    value >= 1_000_000
                      ? `${symbol}${(value / 1_000_000).toFixed(1)}M`
                      : value >= 1_000
                      ? `${symbol}${(value / 1_000).toFixed(0)}K`
                      : `${symbol}${value}`
                  }
                />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.03)" }}
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "16px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                    fontFamily: "Inter, sans-serif",
                    color: "#ffffff",
                  }}
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div style={{
                        background: "#1e293b", borderRadius: 16,
                        border: "1px solid rgba(255,255,255,0.1)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                        padding: "12px 16px", fontFamily: "Inter, sans-serif",
                        minWidth: 200
                      }}>
                        <p style={{ fontWeight: 900, fontSize: 11, color: "#ffffff",
                          textTransform: "uppercase", marginBottom: 8 }}>{label}</p>
                        <p style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
                          <span style={{ fontWeight: 700 }}>Expected Annual Loss: </span>
                          <span style={{ color: "#dc2626", fontWeight: 900 }}>
                            {symbol}{Number(d.impact).toLocaleString()}
                          </span>
                        </p>
                        <p style={{ fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>
                          <span style={{ fontWeight: 700 }}>Worst Case Exposure: </span>
                          <span style={{ color: "#ffffff", fontWeight: 700 }}>
                            {symbol}{Number(d.exposure).toLocaleString()}
                          </span>
                        </p>
                        <p style={{ fontSize: 11, color: "#94a3b8" }}>
                          <span style={{ fontWeight: 700 }}>Probability: </span>
                          <span style={{ fontWeight: 700, color: "#ffffff" }}>
                            {(Number(d.probability) * 100).toFixed(1)}%
                          </span>
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="impact" radius={[8, 8, 0, 0]} barSize={40}>
                  {chartData.map((entry: any, i: number) => (
                    <Cell key={i} fill={getBarColor(entry.impact)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Intelligence Inbox */}
        <motion.div {...stagger(5)}
          className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
            rounded-[2.5rem] p-8 shadow-2xl border border-white/5 flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-sm font-black uppercase tracking-widest text-white">
              Intelligence Inbox
            </h3>
            <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase
              tracking-widest bg-white/10 text-slate-400 border border-white/10">
              Coming Soon
            </span>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
            <div className="w-14 h-14 bg-white/5 border border-white/10
              rounded-2xl flex items-center justify-center mb-4">
              <Clock size={24} className="text-slate-500" />
            </div>
            <p className="text-xs font-black text-white uppercase tracking-tight mb-2">
              Credential Breach Scanning
            </p>
            <p className="text-[10px] font-medium text-slate-400 leading-relaxed max-w-[200px] mb-5">
              Dark web and credential monitoring activating soon. All subscribers
              will be notified when live.
            </p>
            <div className="w-full bg-emerald-900/20 border border-emerald-800/30 rounded-2xl p-4 text-left">
              <p className="text-[9px] font-black uppercase tracking-widest text-emerald-400 mb-3 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full inline-block animate-pulse" />
                When Active — You Will See
              </p>
              <div className="space-y-2.5">
                {[
                  { label: "Exposed Credentials", value: "0 Found",       color: "text-emerald-400" },
                  { label: "Dark Web Mentions",    value: "0 Detected",    color: "text-emerald-400" },
                  { label: "Breach Sources",       value: "Monitoring...", color: "text-emerald-400" },
                  { label: "Last Scan",            value: "Activating",    color: "text-slate-500"   },
                  { label: "Affected Accounts",    value: "0 At Risk",     color: "text-emerald-400" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{item.label}</p>
                    <p className={`text-[10px] font-black uppercase tracking-widest ${item.color}`}>{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

            {/* ── 6A. STRATEGIC MITIGATION PLAN */}
      <motion.div {...stagger(6)}
        className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
          rounded-[2.5rem] p-10 shadow-2xl border border-white/5 border-l-[8px] border-l-red-600">
        <div className="flex items-center gap-5 mb-10">
          <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center
            text-white shadow-lg shadow-red-900/30 flex-shrink-0">
            <Zap size={24} fill="currentColor" />
          </div>
          <div>
            <h3 className="text-xl font-black tracking-tight uppercase text-white">
              Strategic Mitigation Plan
            </h3>
            <p className="text-slate-400 text-[9px] font-black uppercase tracking-[0.25em] mt-1">
              Technical Remediation Roadmap · Infrastructure & Control Hardening
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data?.risk?.mitigation_plan?.filter((item: any) =>
            !item.task?.toLowerCase().includes("training") &&
            !item.task?.toLowerCase().includes("awareness")
          ).length > 0 ? (
            data.risk.mitigation_plan
              .filter((item: any) =>
                !item.task?.toLowerCase().includes("training") &&
                !item.task?.toLowerCase().includes("awareness")
              )
              .map((item: any, i: number) => (
                <RoadmapCard
                  key={i}
                  title={item.task}
                  reduction={item.cost?.toString() || "0"}
                  detail={item.priority + " Priority"}
                  roi_note={item.roi_note}
                  cis_control={item.cis_control}
                  currency={currency}
                />
              ))
          ) : (
            <div className="col-span-2 py-10 text-center bg-slate-50 rounded-2xl
              border-2 border-dashed border-slate-100">
              <p className="text-slate-400 font-black uppercase text-[10px] tracking-widest">
                No Critical Technical Gaps Detected
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* ── 6B. HUMAN CAPITAL RESILIENCE ────────────────────────────── */}
      {hasAssessment && data?.risk && (
      <motion.div {...stagger(7)}
        className={"relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] rounded-[2.5rem] p-10 shadow-2xl border border-white/5 " + (
          data?.risk?.mitigation_plan?.some((item: any) =>
            item.task?.toLowerCase().includes("training") ||
            item.task?.toLowerCase().includes("awareness")
          ) ? "border-l-[8px] border-l-red-600" :
          "border-l-[8px] border-l-emerald-500"
        )}>
        <div className="flex items-center gap-5 mb-10">
          <div className={"w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg flex-shrink-0 " + (
            data?.risk?.mitigation_plan?.some((item: any) =>
              item.task?.toLowerCase().includes("training") ||
              item.task?.toLowerCase().includes("awareness")
            ) ? "bg-red-600" : "bg-emerald-500"
          )}>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-black tracking-tight uppercase text-white">
              Human Capital Resilience Programme
            </h3>
            <p className="text-slate-400 text-[9px] font-black uppercase tracking-[0.25em] mt-1">
              People-Centred Security Investments · Behavioural Risk Reduction
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data?.risk?.mitigation_plan?.filter((item: any) =>
            item.task?.toLowerCase().includes("training") ||
            item.task?.toLowerCase().includes("awareness")
          ).length > 0 ? (
            data.risk.mitigation_plan
              .filter((item: any) =>
                item.task?.toLowerCase().includes("training") ||
                item.task?.toLowerCase().includes("awareness")
              )
              .map((item: any, i: number) => (
                <RoadmapCard
                  key={i}
                  title={item.task}
                  reduction={item.cost?.toString() || "0"}
                  detail={item.priority + " Priority"}
                  roi_note={item.roi_note}
                  cis_control={item.cis_control}
                  currency={currency}
                  accentColor="red"
                />
              ))
          ) : (
            <div className="col-span-2 py-10 text-center bg-emerald-50 rounded-2xl
              border-2 border-dashed border-emerald-200">
              <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <p className="text-emerald-700 font-black uppercase text-[10px] tracking-widest">
                Human Capital Programme Fully Optimised
              </p>
              <p className="text-emerald-500 text-xs font-medium mt-1">
                Monthly training active — phishing resistance maximised
              </p>
            </div>
          )}
        </div>
      </motion.div>
      )}


      {/* ── CYBER-FRAUD FUSION SCORE ─────────────────────────────────── */}
      {(() => {
        const fusion = data?.risk?.cyber_fraud_fusion;
        const tier   = (userData?.subscription_tier || userData?.plan || "starter").toLowerCase();
        const isPro  = tier === "professional" || tier === "enterprise";
        if (!fusion) return null;
        return (
          <motion.div {...stagger(8)}
            className="bg-[#0f172a] rounded-[2.5rem] p-10 border-l-[8px] border-purple-600 shadow-xl">

            {/* Header — always visible */}
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <BarChart3 size={22} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">
                    Cyber-Fraud Fusion Score
                  </h3>
                  <p className="text-slate-500 text-[9px] font-black uppercase tracking-[0.25em] mt-0.5">
                    Where Cyber Risk Becomes Financial Fraud
                  </p>
                </div>
              </div>
              <div className="text-center bg-white/5 rounded-2xl p-5 border border-purple-600 flex-shrink-0">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Fusion Score</p>
                <p className={fusion.fusion_score >= 80 ? "text-4xl font-black tracking-tighter text-emerald-400" : fusion.fusion_score >= 60 ? "text-4xl font-black tracking-tighter text-amber-400" : "text-4xl font-black tracking-tighter text-red-400"}>
                  {fusion.fusion_score}
                </p>
                <p className={fusion.fusion_score >= 80 ? "text-[9px] font-black uppercase tracking-widest mt-1 text-emerald-400" : fusion.fusion_score >= 60 ? "text-[9px] font-black uppercase tracking-widest mt-1 text-amber-400" : "text-[9px] font-black uppercase tracking-widest mt-1 text-red-400"}>
                  {fusion.fusion_label}
                </p>
              </div>
            </div>

            {/* Stats row — always visible */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Active Attack Chains</p>
                <p className={fusion.active_chains_count > 3 ? "text-3xl font-black tracking-tighter text-red-400" : fusion.active_chains_count > 1 ? "text-3xl font-black tracking-tighter text-amber-400" : "text-3xl font-black tracking-tighter text-emerald-400"}>
                  {fusion.active_chains_count}
                </p>
                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-1">
                  of {fusion.active_chains_count + fusion.inactive_chains_count} chains mapped
                </p>
              </div>
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Additional Fraud Exposure</p>
                {isPro ? (
                  <p className="text-3xl font-black tracking-tighter text-red-400">
                    {symbol}{Math.round((fusion.fraud_exposure_usd || 0) / 1000)}K
                  </p>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-3xl font-black tracking-tighter text-slate-600 blur-sm select-none">$999K</p>
                    <Lock size={16} className="text-slate-600 flex-shrink-0" />
                  </div>
                )}
                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-1">Annual fraud risk estimate</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">Fraud Detection Capability</p>
                {isPro ? (
                  <p className={fusion.detection_score >= 70 ? "text-3xl font-black tracking-tighter text-emerald-400" : fusion.detection_score >= 40 ? "text-3xl font-black tracking-tighter text-amber-400" : "text-3xl font-black tracking-tighter text-red-400"}>
                    {fusion.detection_score}%
                  </p>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-3xl font-black tracking-tighter text-slate-600 blur-sm select-none">99%</p>
                    <Lock size={16} className="text-slate-600 flex-shrink-0" />
                  </div>
                )}
                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mt-1">Current control effectiveness</p>
              </div>
            </div>

            {/* Full details — Professional+ only */}
            {isPro ? (
              <>
                {fusion.active_chains?.length > 0 && (
                  <div className="mb-8">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-4">Active Attack Chains</p>
                    <div className="space-y-3">
                      {fusion.active_chains.map((chain: any, i: number) => (
                        <div key={i} className="bg-red-950/30 border border-red-900/40 rounded-2xl p-5">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className="text-[9px] font-black uppercase tracking-widest text-red-400 bg-red-950/50 px-3 py-1 rounded-full border border-red-900/40">{chain.severity}</span>
                            <span className="text-sm font-black text-white uppercase tracking-tight">{chain.cyber_entry}</span>
                            <span className="text-red-500 font-black">→</span>
                            <span className="text-sm font-black text-red-400 uppercase tracking-tight">{chain.fraud_exit}</span>
                          </div>
                          <p className="text-xs font-medium text-slate-400 leading-relaxed">{chain.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {fusion.recommendations?.length > 0 && (
                  <div>
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-4">Priority Actions to Break Active Chains</p>
                    <div className="space-y-3">
                      {fusion.recommendations.map((rec: any, i: number) => (
                        <div key={i} className="bg-purple-950/20 border border-purple-900/30 rounded-2xl p-5 flex items-start gap-4">
                          <div className="w-7 h-7 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-white font-black text-xs">{i + 1}</span>
                          </div>
                          <div>
                            <p className="text-xs font-black text-white uppercase tracking-tight mb-1">Break: {rec.chain}</p>
                            <p className="text-xs font-medium text-slate-400 leading-relaxed">{rec.action}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {fusion.active_chains_count === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-16 h-16 bg-emerald-950/30 border-2 border-emerald-900/40 rounded-2xl flex items-center justify-center mb-4">
                      <ShieldCheck size={28} className="text-emerald-400" />
                    </div>
                    <p className="text-base font-black text-emerald-400 uppercase tracking-tight mb-2">No Active Attack Chains Detected</p>
                    <p className="text-sm font-medium text-slate-500 max-w-md leading-relaxed">Your current security controls have broken all mapped cyber-to-fraud attack chains.</p>
                  </div>
                )}
              </>
            ) : (
              /* Starter — upgrade gate */
              <div className="border-2 border-dashed border-purple-800/40 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 bg-purple-950/40 border border-purple-800/40 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Lock size={24} className="text-purple-400" />
                </div>
                <p className="text-base font-black text-white uppercase tracking-tight mb-2">
                  {fusion.active_chains_count} Active Attack Chains Detected
                </p>
                <p className="text-sm font-medium text-slate-400 leading-relaxed mb-6 max-w-md mx-auto">
                  Your organisation has {fusion.active_chains_count} active cyber-to-fraud attack chains.
                  Upgrade to Professional to see exactly which chains are active, your full fraud exposure breakdown, and priority actions to neutralise each threat.
                </p>
                <button
                  onClick={() => window.location.href = "/settings"}
                  className="inline-flex items-center gap-2 bg-purple-600 text-white px-8 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-purple-500 transition-colors">
                  Upgrade to Professional <ChevronRight size={14} />
                </button>
                <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest mt-3">
                  Professional plan — $499/month
                </p>
              </div>
            )}

          </motion.div>
        );
      })()}

      {/* ── RESIDUAL RISK NOTICE ────────────────────────────────────── */}
      <motion.div {...stagger(8)}
        className="bg-[#0f172a] rounded-2xl p-6 border border-white/10 border-l-4 border-l-slate-600">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-2">
          Residual Risk Notice
        </p>
        <p className="text-xs font-medium text-slate-400 leading-relaxed">
          A 20% residual risk factor is retained...
          <span className="font-bold text-white">IBM Cost of a Data Breach 2024</span> and{" "}
          <span className="font-bold text-white">NIST SP 800-30</span>
          for Tier 4 maturity organisations.
        </p>
      </motion.div>

      {/* ── 7. DOMAIN INTELLIGENCE ──────────────────────────────────── */}
      {(() => {
        const threats = data?.scan_results || data?.external_threats;
        const statusColor: Record<string, string> = {
          CRITICAL:  "text-red-600 bg-red-50 border-red-200",
          HIGH:      "text-orange-600 bg-orange-50 border-orange-200",
          MEDIUM:    "text-amber-600 bg-amber-50 border-amber-200",
          CLEAR:     "text-emerald-600 bg-emerald-50 border-emerald-200",
          PROTECTED: "text-emerald-400 bg-emerald-900/40 border-emerald-700/50",
          PENDING:   "text-slate-400 bg-white/5 border-white/10",
        };
        const riskColor: Record<string, string> = {
          CRITICAL: "text-red-600 bg-red-50 border-red-200",
          HIGH:     "text-orange-600 bg-orange-50 border-orange-200",
          MEDIUM:   "text-amber-600 bg-amber-50 border-amber-200",
          LOW:      "text-blue-600 bg-blue-50 border-blue-200",
          INFO:     "text-slate-400 bg-white/5 border-white/10",
        };
        const overallStatus: string = threats?.status || "PENDING";
        const isActive = !!(
          threats?.domain && (
            threats?.shodan_active ||
            (threats?.risk_findings?.length ?? 0) > 0 ||
            (threats?.open_ports?.length ?? 0) > 0 ||
            (threats?.cve_findings?.length ?? 0) > 0 ||
            threats?.status === "CLEAR" ||
            threats?.status === "PROTECTED" ||
            threats?.status === "MONITORED" ||
            threats?.ip
          )
        );

        return (
          <motion.div {...stagger(9)}
            className="relative overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a]
              p-10 rounded-[2.5rem] border border-white/5 shadow-2xl">

            {/* Header */}
            <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-red-500 mb-1">
                  External Threat Surface
                </p>
                <h3 className="text-2xl font-black text-white uppercase tracking-tighter">
                  Domain Intelligence
                </h3>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                  Domain: {scannedDomain}
                  {threats?.ip && <span className="ml-2 text-slate-300">· IP: {threats.ip}</span>}
                  {threats?.country && <span className="ml-2 text-slate-300">· {threats.country}</span>}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <button onClick={handleRescan} disabled={rescanning}
                  className="px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest
                    border border-white/20 bg-white/10 text-white hover:bg-white/20
                    transition-all disabled:opacity-50 flex items-center gap-2">
                  {rescanning
                    ? <><Loader2 size={11} className="animate-spin" /> Scanning...</>
                    : <><RefreshCw size={11} /> Rescan</>}
                </button>
                <span className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border flex-shrink-0 ${statusColor[overallStatus] || statusColor.PENDING}`}>
                  {isActive ? overallStatus : "Activating"}
                </span>
              </div>
            </div>

            {isActive ? (
              <>
                {/* Stats grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: "Open Ports",    value: threats?.risk_findings?.length ?? threats?.open_ports?.length ?? 0, danger: (threats?.risk_findings?.length ?? 0) > 0 },
                    { label: "Vulnerabilities", value: threats?.critical_count ?? 0,                                       danger: (threats?.critical_count ?? 0) > 0 },
                    { label: "Breaches",      value: threats?.total_leaks ?? 0,                                            danger: (threats?.total_leaks ?? 0) > 0 },
                    { label: "CVEs Found",    value: threats?.cve_findings?.length ?? 0,                                   danger: (threats?.cve_findings?.length ?? 0) > 0 },
                  ].map((stat, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl p-4 border border-white/10">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">{stat.label}</p>
                      <p className={`text-2xl font-black ${stat.danger ? "text-red-400" : "text-emerald-400"}`}>{stat.value}</p>
                    </div>
                  ))}
                </div>

                {/* Open Ports / Risk Findings from Shodan */}
                {(threats?.risk_findings?.length ?? 0) > 0 && (
                  <div className="mb-6">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Exposed Services & Open Ports</p>
                    <div className="space-y-2">
                      {threats.risk_findings.slice(0, 10).map((f: any, i: number) => (
                        <div key={i} className="flex items-center justify-between gap-4 p-3 bg-white/5 rounded-xl border border-white/10">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className="text-[10px] font-black text-slate-400 bg-slate-200 rounded-lg px-2 py-1 font-mono flex-shrink-0">
                              :{f.port}
                            </span>
                            <div className="min-w-0">
                              <p className="text-xs font-black text-slate-900 uppercase tracking-tight truncate">
                                {(f.name || f.service || `Port ${f.port}`).replace(`SERVICE ON PORT ${f.port}`, '').trim() || `Port ${f.port}`}
                              </p>
                              {f.reason && <p className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">{f.reason}</p>}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border flex-shrink-0 ${riskColor[f.risk || f.risk_level] || riskColor.INFO}`}>
                            {f.risk || f.risk_level || "INFO"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CVE Vulnerabilities */}
                {(threats?.cve_findings?.length ?? 0) > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">CVE Vulnerabilities Detected</p>
                      <p className="text-[9px] font-black text-red-400 uppercase tracking-widest">
                        Total Exposure: {symbol}{threats.cve_findings.reduce((sum: number, c: any) => sum + (c.est_cost_max || 0), 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="space-y-2">
                      {threats.cve_findings.slice(0, 6).map((c: any, i: number) => (
                        <div key={i} className="p-4 bg-red-950/30 rounded-xl border border-red-900/30">
                          <div className="flex items-center justify-between gap-4 mb-2">
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-black text-white uppercase tracking-tight">{c.cve || c.cve_id || c.id || "CVE"}</p>
                              {c.cvss && <span className="text-[9px] font-black text-slate-400">CVSS {c.cvss}</span>}
                            </div>
                            <span className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest flex-shrink-0 ${
                              c.severity === "CRITICAL" ? "bg-red-900/50 text-red-400 border border-red-700/50" :
                              c.severity === "HIGH" ? "bg-amber-900/50 text-amber-400 border border-amber-700/50" :
                              "bg-blue-900/50 text-blue-400 border border-blue-700/50"
                            }`}>
                              {c.severity || "HIGH"}
                            </span>
                          </div>
                          {(c.summary || c.description || c.desc) && (
                            <p className="text-[10px] font-medium text-slate-400 mb-3 leading-relaxed">
                              {c.summary || c.description || c.desc}
                            </p>
                          )}
                          {c.est_cost_min && (
                            <div className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2">
                              <div>
                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">Estimated Exploitation Cost</p>
                                <p className="text-sm font-black text-red-400">{symbol}{c.est_cost_min.toLocaleString()} — {symbol}{c.est_cost_max.toLocaleString()}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">Exploit Likelihood</p>
                                <p className={`text-xs font-black ${
                                  c.exploit_likelihood === "Very High" ? "text-red-400" :
                                  c.exploit_likelihood === "High" ? "text-amber-400" : "text-blue-400"
                                }`}>{c.exploit_likelihood || "High"}</p>
                              </div>
                            </div>
                          )}
                          {c.remediation && (
                            <p className="text-[9px] font-bold text-slate-500 mt-2">→ {c.remediation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Breach names from HIBP */}
                {(threats?.breach_names?.length ?? 0) > 0 && (
                  <div className="mb-6">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">Credential Breaches Detected</p>
                    <div className="flex flex-wrap gap-2">
                      {threats.breach_names.map((b: string, i: number) => (
                        <span key={i} className="px-3 py-1.5 bg-red-50 border border-red-200 rounded-xl text-xs font-black text-red-600 uppercase tracking-wider">{b}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* All clear — nothing found */}
                {(threats?.risk_findings?.length ?? 0) === 0 &&
                 (threats?.cve_findings?.length ?? 0) === 0 &&
                 (threats?.breach_names?.length ?? 0) === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <div className="w-16 h-16 bg-emerald-50 border-2 border-emerald-200 rounded-[1.5rem] flex items-center justify-center mb-4">
                      <ShieldCheck size={28} className="text-emerald-500" />
                    </div>
                    <h4 className="text-sm font-black text-emerald-700 uppercase tracking-tight mb-2">
                      {threats?.status === "CLEAR" ? "No Threats Detected" : "Scan Complete"}
                    </h4>
                    <p className="text-sm font-medium text-slate-400 leading-relaxed max-w-md">
                      {threats?.status === "CLEAR"
                        ? `External scan of ${scannedDomain} found no exposed services or vulnerabilities.`
                        : `VicShield has scanned ${scannedDomain}. No open ports or CVEs detected.`}
                    </p>
                  </div>
                )}
              </>
            ) : isActive && threats?.status === "CLEAR" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 bg-emerald-50 border-2 border-emerald-200 rounded-[1.5rem] flex items-center justify-center mb-5">
                  <ShieldCheck size={32} className="text-emerald-500" />
                </div>
                <h4 className="text-base font-black text-emerald-700 uppercase tracking-tight mb-2">No Threats Detected</h4>
                <p className="text-sm font-medium text-slate-400 leading-relaxed max-w-md">
                  External scan of {scannedDomain} found no exposed services or credential breaches.
                </p>
              </div>
            ) : isActive && threats?.status === "PROTECTED" ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 bg-blue-50 border-2 border-blue-200 rounded-[1.5rem] flex items-center justify-center mb-5">
                  <ShieldCheck size={32} className="text-blue-500" />
                </div>
                <h4 className="text-base font-black text-blue-700 uppercase tracking-tight mb-2">
                  Domain Protected by {threats?.org?.includes("Cloudflare") ? "Cloudflare" : threats?.org?.includes("Akamai") ? "Akamai" : threats?.org?.includes("Fastly") ? "Fastly" : threats?.org?.includes("AWS") ? "AWS CloudFront" : threats?.org || "CDN"} CDN
                </h4>
                <p className="text-sm font-medium text-slate-400 leading-relaxed max-w-md">
                  {scannedDomain} is behind a CDN provider which masks the origin server.
                  Direct infrastructure scanning is limited. This is a positive security signal —
                  the CDN provides DDoS protection and hides your real IP address.
                </p>
                <p className="text-xs font-black uppercase tracking-widest text-blue-500 mt-3">
                  IP: {threats?.ip} · {threats?.org}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 bg-white/5 border-2 border-dashed border-white/10 rounded-[1.5rem] flex items-center justify-center mb-5">
                  <Globe size={32} className="text-slate-400" />
                </div>
                <h4 className="text-base font-black text-slate-900 uppercase tracking-tight mb-2">External Scan Active</h4>
                <p className="text-sm font-medium text-slate-400 leading-relaxed max-w-md">
                  VicShield is scanning {scannedDomain} for exposed services and vulnerabilities.
                  Results will appear after your next assessment.
                </p>
              </div>
            )}
          </motion.div>
        );
      })()}

    </div>
  );
}
